import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminDashboard.module.css';
import { Button } from '../../components/common/Button/Button';
import { useAdmin } from './AdminContext';
import { useToast } from '../../contexts/ToastContext';
import { projectsAPI } from '../../services/api';
import { AdminTable, ColumnDef, FilterDef } from './AdminTable';

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { projects, loading, refreshData } = useAdmin();

  const handleBulkDelete = useCallback(async (ids: string[]) => {
    if (window.confirm(`Weet je zeker dat je ${ids.length} project(en) wilt verwijderen?`)) {
      try {
        await Promise.all(ids.map((id) => projectsAPI.delete(id)));
        toast.success(`${ids.length} project(en) verwijderd`);
        refreshData();
      } catch (error: any) {
        toast.error('Verwijderen mislukt', error.response?.data?.error || 'Er ging iets mis.');
      }
    }
  }, [refreshData, toast]);

  const columns: ColumnDef<any>[] = [
    {
      key: 'name',
      label: 'Naam',
      sortable: true,
      render: (project) => <span style={{ fontWeight: 600 }}>{project.name}</span>,
      exportValue: (project) => project.name || '',
    },
    {
      key: 'company',
      label: 'Klant',
      sortable: true,
      render: (project) => project.company?.name || '-',
      exportValue: (project) => project.company?.name || '',
    },
    {
      key: 'location',
      label: 'Locatie',
      render: (project) =>
        typeof project.location === 'object'
          ? project.location?.city || '-'
          : project.location || '-',
      exportValue: (project) =>
        typeof project.location === 'object'
          ? project.location?.city || ''
          : project.location || '',
    },
    {
      key: 'status',
      label: 'Status',
      render: (project) => (
        <span className={`${styles.statusBadge} ${styles[`status${project.status}`]}`}>
          {project.status}
        </span>
      ),
      exportValue: (project) => project.status || '',
    },
    {
      key: 'startDate',
      label: 'Startdatum',
      sortable: true,
      render: (project) =>
        project.startDate ? new Date(project.startDate).toLocaleDateString('nl-NL') : '-',
      exportValue: (project) =>
        project.startDate ? new Date(project.startDate).toLocaleDateString('nl-NL') : '',
    },
    {
      key: 'endDate',
      label: 'Einddatum',
      sortable: true,
      render: (project) =>
        project.endDate ? new Date(project.endDate).toLocaleDateString('nl-NL') : '-',
      exportValue: (project) =>
        project.endDate ? new Date(project.endDate).toLocaleDateString('nl-NL') : '',
    },
    {
      key: 'documents',
      label: 'Documenten',
      sortable: true,
      render: (project) => (
        <span style={{ fontWeight: 600 }}>{project.documents?.length || 0}</span>
      ),
      exportValue: (project) => String(project.documents?.length || 0),
    },
  ];

  const statuses = Array.from(new Set(projects.map((p: any) => p.status).filter(Boolean)));
  const companyNames = Array.from(new Set(projects.map((p: any) => p.company?.name).filter(Boolean)));

  const filters: FilterDef[] = [
    {
      key: 'status',
      label: 'Alle statussen',
      options: statuses.map((s: string) => ({ value: s, label: s })),
    },
    {
      key: 'company',
      label: 'Alle klanten',
      options: companyNames.map((n: string) => ({ value: n, label: n })),
    },
  ];

  return (
    <AdminTable<any>
      data={projects}
      columns={columns}
      filters={filters}
      filterFn={(project, activeFilters) => {
        if (activeFilters.status && project.status !== activeFilters.status) return false;
        if (activeFilters.company && project.company?.name !== activeFilters.company) return false;
        return true;
      }}
      searchFn={(project, query) =>
        (project.name || '').toLowerCase().includes(query) ||
        (project.company?.name || '').toLowerCase().includes(query) ||
        (typeof project.location === 'object'
          ? (project.location?.city || '')
          : (project.location || '')
        ).toLowerCase().includes(query)
      }
      onRowClick={(project) => navigate(`/admin/projects/${project._id || project.id}`)}
      getId={(project) => project._id || project.id}
      onBulkDelete={handleBulkDelete}
      exportFilename="projecten"
      loading={loading}
      emptyIcon={
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      }
      emptyTitle="Nog geen projecten"
      emptyDescription="Maak je eerste project aan om te beginnen."
      emptyAction={
        <Button onClick={() => navigate('/admin/projects/new')}>
          + Nieuw project
        </Button>
      }
      headerTitle="Projecten"
      headerAction={
        <Button onClick={() => navigate('/admin/projects/new')}>
          + Nieuw project
        </Button>
      }
    />
  );
};
